<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, watch, computed, nextTick, useId } from 'vue';
import { useFocusTrap } from '@/composables/useFocusTrap';
import { useKeyBindings } from '@/composables/useKeyBindings';
import { acquireScrollLock, releaseScrollLock } from './_scrollLock';
import { log } from '@/lib/log';

// Modal primitive — backdrop, scroll-lock, focus trap, Esc to close.
//
// Body lock applies `overflow: hidden` to <html> while the modal is open
// and restores it on close. Multiple stacked modals coordinate via a
// shared open-count counter (see `_scrollLock.ts`) so closing the inner
// modal doesn't unlock scroll while the outer is still up.

interface Props {
    /** v-model:open binding. */
    open: boolean;
    title?: string | undefined;
    /** When false, clicking the backdrop is a no-op. Default true. */
    dismissOnBackdrop?: boolean;
    /** When false, Esc is ignored. Default true. */
    dismissOnEsc?: boolean;
    /** Layer above PiP, below toast/focus. Use 'top' for modal-within-modal. */
    layer?: 'normal' | 'top';
    /** ARIA labeller — id of an external element when `title` is empty. */
    ariaLabelledby?: string;
    /** Atlas Phase 7 — on a phone, dock to the bottom as a full-width sheet
     *  (top-only radius, slides up) instead of a centred card. Desktop unchanged. */
    sheet?: boolean;
    /** Wider content surface for dense detail views. */
    size?: 'default' | 'wide';
}

const props = withDefaults(defineProps<Props>(), {
    dismissOnBackdrop: true,
    dismissOnEsc: true,
    layer: 'normal',
    sheet: false,
    size: 'default',
});

const emit = defineEmits<{
    'update:open': [value: boolean];
    close: [];
}>();

const dialogRef = ref<HTMLElement | null>(null);

const focusTrap = useFocusTrap(dialogRef, { autoFocus: true });

useKeyBindings([
    {
        key: 'Escape',
        when: () => props.open && props.dismissOnEsc,
        handler: (ev) => {
            ev.preventDefault();
            requestClose('esc');
        },
        label: 'Close dialog',
    },
], { scope: 'Dialog' });

function requestClose(reason: 'esc' | 'backdrop' | 'button') {
    log.debug('modal.close', { reason });
    emit('update:open', false);
    emit('close');
}

function onBackdropClick() {
    if (props.dismissOnBackdrop) requestClose('backdrop');
}

const wasOpen = ref(false);

watch(
    () => props.open,
    (isOpen) => {
        if (isOpen && !wasOpen.value) {
            acquireScrollLock();
            wasOpen.value = true;
            void nextTick(() => focusTrap.install());
        } else if (!isOpen && wasOpen.value) {
            focusTrap.release();
            releaseScrollLock();
            wasOpen.value = false;
        }
    },
);

onMounted(() => {
    if (props.open) {
        acquireScrollLock();
        wasOpen.value = true;
    }
});

onBeforeUnmount(() => {
    if (wasOpen.value) {
        releaseScrollLock();
        wasOpen.value = false;
    }
});

const layerClass = computed(() => `layer-${props.layer}`);
const sizeClass = computed(() => `size-${props.size}`);
const titleId = useId();
const labelledby = computed(() => props.ariaLabelledby ?? (props.title ? titleId : undefined));
</script>

<template>
    <Teleport to="body">
        <Transition name="modal">
            <div
                v-if="open"
                :class="['modal-root', layerClass, { sheet }]"
                role="presentation"
                @mousedown.self="onBackdropClick"
            >
                <div
                    ref="dialogRef"
                    :class="['dialog', sizeClass]"
                    role="dialog"
                    aria-modal="true"
                    :aria-labelledby="labelledby"
                    tabindex="-1"
                >
                    <!-- Drag-handle pill (atlas-mobile.jsx sheet frames) — purely a
                         bottom-sheet affordance; CSS shows it only in sheet mode on
                         mobile widths. -->
                    <div v-if="sheet" class="sheet-handle" aria-hidden="true"></div>
                    <header v-if="title || $slots.header" class="head">
                        <h2 v-if="title" :id="titleId" class="title">{{ title }}</h2>
                        <slot name="header" />
                        <button class="close" type="button" aria-label="Close" @click="requestClose('button')">×</button>
                    </header>
                    <div class="body"><slot /></div>
                    <footer v-if="$slots.footer" class="foot"><slot name="footer" /></footer>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.modal-root {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.65);
    padding: var(--space-md);
    overscroll-behavior: contain;
}

.modal-root.layer-normal {
    z-index: var(--z-modal);
}

.modal-root.layer-top {
    z-index: var(--z-modal-top);
}

.dialog {
    background: var(--color-bg-secondary);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    width: min(560px, 100%);
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    outline: none;
}

.dialog.size-wide {
    width: min(960px, 100%);
}

.head {
    display: flex;
    align-items: center;
    gap: var(--space-md);
    padding: var(--space-md) var(--space-lg);
    border-bottom: 1px solid var(--color-border);
}

.title {
    margin: 0;
    flex: 1;
    font-size: 16px;
    font-weight: 600;
}

.close {
    color: var(--color-text-muted);
    font-size: 22px;
    line-height: 1;
    min-width: 44px;
    min-height: 44px;
    padding: 0 var(--space-xs);
    border-radius: var(--radius-sm);
}

.close:hover,
.close:focus-visible {
    color: var(--color-text-primary);
    background: var(--color-bg-hover);
}

.body {
    padding: var(--space-lg);
    overflow-y: auto;
    flex: 1;
}

.foot {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-sm);
    padding: var(--space-md) var(--space-lg);
    border-top: 1px solid var(--color-border);
}

/* Desktop keeps the centred card — the handle is a sheet-only affordance,
   revealed inside the --bp-lg media query below. */
.sheet-handle {
    display: none;
}

.modal-enter-active,
.modal-leave-active {
    transition:
        opacity var(--transition-fast),
        transform var(--transition-fast);
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-from .dialog,
.modal-leave-to .dialog {
    transform: translateY(8px) scale(0.98);
}

/* Atlas Phase 7 — touch-tier bottom-sheet variant. Across the whole touch range
   (phone AND large-mobile, i.e. everything below --bp-xl) the sheet docks to the
   bottom edge full-width with a top-only radius and slides up; desktop keeps the
   centred card. Widened from --bp-sm to --bp-lg in the remediation pass so
   "sheet" means the same thing as "mobile layout" (#16), and to --bp-xl by
   mobile-foldable-redesign §2.2 now that "mobile layout" reaches 1023. */
@media (max-width: 1023px) { /* --bp-xl */
    .modal-root.sheet {
        align-items: flex-end;
        padding: 0;
    }
    .modal-root.sheet .dialog {
        width: 100%;
        max-width: none;
        max-height: 92vh;
        border-bottom: 0;
        border-left: 0;
        border-right: 0;
        border-radius: var(--radius-lg) var(--radius-lg) 0 0;
        padding-bottom: var(--safe-area-inset-bottom);
    }
    .modal-root.sheet.modal-enter-from .dialog,
    .modal-root.sheet.modal-leave-to .dialog {
        transform: translateY(100%);
    }
    .modal-root.sheet .sheet-handle {
        display: block;
        flex: 0 0 auto;
        width: 36px;
        height: 4px;
        margin: var(--space-sm) auto 0;
        border-radius: var(--radius-full);
        background: var(--color-border);
    }
}

@media (prefers-reduced-motion: reduce) { /* feature: prefers-reduced-motion */
    .modal-enter-active,
    .modal-leave-active {
        transition: none;
    }
}
</style>
